import { ReactNode, useEffect, useRef, useState } from "react";
import chroma from "chroma-js";
import styled from "styled-components";
import FadeImage from "../FadeImage/FadeImage";
import {
  getAbsoluteFill,
  getAbsoluteFillBottom,
  getAbsoluteFillTop,
  getFlexCenter,
} from "../../styles";
import {
  getHeading6BoldTypography,
  getParagraph14BoldTypography,
} from "../../Typography";

const textPadding = 37;
const textPaddingBottom = 52;

const BannerWrapper = styled.div`
  width: 100%;
  height: 350px;
  background: ${(props) => props.theme.lightBackgroundColor};
  border-radius: ${(props) => props.theme.borderRadius.md};
  overflow: hidden;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    height: 370px;
  }
`;

const Banner = styled(FadeImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: relative;
`;

const TextWrapper = styled.div<{
  isExpanded: boolean;
  height: number | null;
}>`
  background: linear-gradient(
    rgba(0, 0, 0, 0),
    ${({ theme }) => chroma(theme.secondaryColor).alpha(0.8).hex()}
  );
  ${getAbsoluteFillBottom};
  height: ${({ height }) => (height ? `${height}px` : "auto")};
  max-height: 100%;
  overflow-y: ${({ isExpanded }) => (isExpanded ? "auto" : "none")};
  padding: ${textPadding}px ${textPadding}px ${textPaddingBottom}px
    ${textPadding}px;
  transition: height 150ms ease-in;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Header = styled.h6`
  ${getHeading6BoldTypography};
  color: ${(props) => props.theme.primaryColor};
  padding-bottom: 16px;
`;

export const DescriptionWrapper = styled.div`
  width: 100%;
  position: relative;
`;

export const Description = styled.div<{ isExpanded: boolean }>`
  white-space: break-spaces;
  ${getAbsoluteFillTop};
  position: ${({ isExpanded }) => (isExpanded ? "relative" : "absolute")};
  opacity: ${({ isExpanded }) => (isExpanded ? 1 : 0)};
  font-size: ${(props) => props.theme.paragraph.lg};
  color: ${(props) => props.theme.primaryColor};
  line-height: 20px;
  transition: opacity 150ms ease-in;
`;

export const DescriptionPreview = styled(Description)`
  position: ${({ isExpanded }) => (isExpanded ? "absolute" : "relative")};
  opacity: ${({ isExpanded }) => (isExpanded ? 0 : 1)};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 40px;
`;

const ReadMoreWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 1;
  position: absolute;
  left: 0;
  right: 0;
  bottom: -32px;
`;

const ReadMore = styled.span`
  font-size: ${(props) => props.theme.paragraph.lg};
  ${getParagraph14BoldTypography};
  color: ${(props) => props.theme.primaryColor};
  cursor: pointer;
`;

const EmptyStateWrapper = styled.div`
  ${getAbsoluteFill};
  ${getFlexCenter};
  z-index: 1;
`;

interface Props {
  header?: string;
  description?: string | null;
  defaultPhotoUrl: string;
  photoUrl?: string;
  resizeWidth?: number;
  EmptyStateComponent?: ReactNode;
}

const AboutPhoto = ({
  header,
  description,
  photoUrl,
  defaultPhotoUrl,
  EmptyStateComponent,
  resizeWidth,
}: Props) => {
  const [observer, setObserver] = useState<ResizeObserver>();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isReadMoreVisible, setIsReadMoreVisible] = useState<boolean>(false);
  const [textHeight, setTextHeight] = useState<number | null>(null);
  const bannerContainer = useRef<HTMLDivElement>(null);
  const headerContainer = useRef<HTMLDivElement>(null);
  const textContainer = useRef<HTMLDivElement>(null);
  const descriptionContainer = useRef<HTMLDivElement>(null);
  const descriptionPreviewContainer = useRef<HTMLDivElement>(null);
  const displayUrl = photoUrl || defaultPhotoUrl;
  const showEmptyState = !photoUrl && !description && !!EmptyStateComponent;

  useEffect(() => {
    setObserver(
      new ResizeObserver((entries) => {
        const container = entries[0]?.target;
        if (container) {
          setIsReadMoreVisible(container.scrollHeight > container.clientHeight);
        }
      })
    );
  }, []);

  useEffect(() => {
    if (observer && descriptionPreviewContainer.current) {
      observer.observe(descriptionPreviewContainer.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, [description, observer]);

  useEffect(() => {
    if (textContainer.current) {
      textContainer.current.scrollTop = 0;
    }
  }, [isExpanded]);

  const toggleExpand = () => {
    const currentHeight = textContainer.current!.clientHeight;
    const bannerHeight = bannerContainer.current!.clientHeight;
    const headerHeight = headerContainer.current?.clientHeight || 0;
    const fullTextHeight = descriptionContainer.current!.clientHeight;
    const previewTextHeight = descriptionPreviewContainer.current!.clientHeight;
    setTextHeight(currentHeight);
    setIsExpanded((v) => !v);

    setTimeout(() => {
      const baseHeight = isExpanded ? previewTextHeight : fullTextHeight;
      setTextHeight(
        Math.min(
          baseHeight + headerHeight + textPadding + textPaddingBottom,
          bannerHeight
        )
      );
    });
  };

  const onTextTransitionEnd = () => setTextHeight(null);

  return (
    <BannerWrapper ref={bannerContainer}>
      {displayUrl && <Banner src={displayUrl} resizeWidth={resizeWidth} />}
      {description && (
        <TextWrapper
          isExpanded={isExpanded}
          height={textHeight}
          onTransitionEnd={onTextTransitionEnd}
          ref={textContainer}
        >
          {header && <Header ref={headerContainer}>{header}</Header>}
          <DescriptionWrapper>
            <Description isExpanded={isExpanded} ref={descriptionContainer}>
              {description}
            </Description>
            <DescriptionPreview
              isExpanded={isExpanded}
              ref={descriptionPreviewContainer}
            >
              {description}
            </DescriptionPreview>
            {isReadMoreVisible && (
              <ReadMoreWrapper>
                <ReadMore onClick={toggleExpand}>
                  {isExpanded ? "Read less" : "Read more"}
                </ReadMore>
              </ReadMoreWrapper>
            )}
          </DescriptionWrapper>
        </TextWrapper>
      )}
      {showEmptyState && (
        <EmptyStateWrapper>{EmptyStateComponent}</EmptyStateWrapper>
      )}
    </BannerWrapper>
  );
};

export default AboutPhoto;
